import { useQuery } from '@tanstack/vue-query';
import apiInterceptor from '@/shared/apiInterceptor';
import { environment } from '@/shared/env';

/**
 * Returns TanStack Query options for the folders list endpoint.
 */
export const foldersQueryOptions = () => ({
  queryKey: ['bot-folders'],
  queryFn: async () => {
    const r = await apiInterceptor.get(environment('VUE_APP_API_URL') + 'bot-builder/v1/folders/', { skip_limit: true });
    return r?.folders || [];
  },
  staleTime: Infinity,
});

/**
 * Composable wrapper for use inside <script setup> components.
 */
export function useFolders() {
  return useQuery(foldersQueryOptions());
}

/**
 * Returns TanStack Query options for bots within a specific named folder.
 * Fetches all bots for the folder (no server-side pagination) for client-side pagination.
 *
 * @param {object} options
 * @param {string} options.folderId - The folder ID to fetch bots for
 */
export const folderBotsQueryOptions = ({ folderId }) => ({
  queryKey: ['bots', { folderId }],
  queryFn: async () => {
    const r = await apiInterceptor.get(
      environment('VUE_APP_API_URL') + `bot-builder/v1/folders/${folderId}/bots`,
      { params: { limit: 10000, offset: 0 } }
    );
    return r?.bots || [];
  },
  staleTime: Infinity,
});

/**
 * Composable wrapper for use inside <script setup> components.
 */
export function useFolderBots(options = {}) {
  return useQuery(folderBotsQueryOptions(options));
}

/**
 * Returns TanStack Query options for the bots list endpoint.
 * Each unique { isActive, channel } combination gets its own cache entry.
 *
 * @param {object} [options]
 * @param {boolean} [options.isActive=false] - Adds `?is_active=true` filter
 * @param {string|null} [options.channel=null] - Adds `&sales_channel=<channel>` filter
 * @param {string|null} [options.folderId=null] - Adds `&folder=<folderId>` filter
 */
export const botsQueryOptions = ({ isActive = false, channel = null, folderId = null } = {}) => {
  let url = 'bot-builder/v1/bots/';
  const params = [];
  if (isActive) params.push('is_active=true');
  if (channel) params.push(`sales_channel=${channel}`);
  if (folderId) params.push(`folder=${folderId}`);
  if (params.length) url += '?' + params.join('&');

  return {
    queryKey: ['bots', { isActive, channel: channel ?? null, folderId: folderId ?? null }],
    queryFn: async () => {
      const r = await apiInterceptor.get(environment('VUE_APP_API_URL') + url);
      return r?.bots || [];
    },
    staleTime: Infinity,
  };
};

/**
 * Composable wrapper for use inside <script setup> components.
 */
export function useBots(options = {}) {
  return useQuery(botsQueryOptions(options));
}
